/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Query } from 'react-apollo';

import {
    object, string, bool,
} from 'prop-types';
import mbpLogger from 'mbp-logger';
// components
import UnderNavTextWidgetContainer from '../GraphqlCommonComponents/GraphqlUnderNavTextWidget/GraphqlUnderNavTextWidgetContainer';
import GraphqlCategorySkeleton from '../GraphqlSkeletonComponents/CategoryPageSkeleton/CategoryPageSkeleton';
import GraphqlCategoryBuilder from './GraphqlCategoryBuilder';

// helpers, redux, queries
import {
    getUserSubmittedProductFilterZipcode, getLocationType, getIsBot, getCurrencyData,
} from '../../../../state/ducks/App/App-Selectors';
import { getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { GRAPHQL_ENV } from '../../../gql';
import { coreBrowsingPageViewSuccessTest } from '../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../helpers/tracking/hocs/withPageView';
import findCategoryPageByURL from '../../../gql/queries/findCategoryPageByURL';

const stopReRender = (prevProps, nextProps) => {
    // This component should only run its query a single time on initial category page load.
    // If zipcode is set before cat page the query should still run with this filtering info
    // To prevent this larger query from rerunning on subsequent changes of location type or zipcode
    // The component is wrapped in react.memo and this function is provided as a param.
    // If this function returns true the component does not rerender and the query is not rerun
    if (prevProps.locationType !== nextProps.locationType) return true;
    if (prevProps.userSubmittedZip !== nextProps.userSubmittedZip) return true;
    return false;
};
// memoized to prevent re-renders, stopReRender ^
const GraphqlCategoryPageContainer = React.memo(({
    brand, page, userSubmittedZip, locationType, presentation_family, isBot, currency,
}) => {
    const CATEGORY_PAGE_QUERY = findCategoryPageByURL(brand.domain, page.path, page.contentType, userSubmittedZip, locationType, isBot, currency.currencyName);
    return (
        <Query query={CATEGORY_PAGE_QUERY} fetchPolicy="cache-and-network">
            {({ loading, data, error }) => {
                if (loading) {
                    return <GraphqlCategorySkeleton />;
                }

                if (error) {
                    mbpLogger.logError({
                        query: CATEGORY_PAGE_QUERY,
                        component: 'GraphqlCategoryPageContainer.js',
                        message: 'Error executing findCategoryPageByUrl query',
                        env: GRAPHQL_ENV,
                        error,
                    });
                    return null;
                }

                if (!data) {
                    mbpLogger.logWarning({
                        query: CATEGORY_PAGE_QUERY,
                        component: 'GraphqlCategoryPageContainer.js',
                        message: 'No data returned for Category Page Query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }
                const categoryData = data?.findCategoryPageByUrl;
                // Set category name and shortDescription for Google Analytics' ec:impression name
                if (categoryData?.category && categoryData.content?.entries?.[0]?.name) {
                    const categoryName = categoryData.content.entries[0].name;
                    if (!categoryData.category.name) {
                        categoryData.category.name = categoryName;
                    }
                    if (!categoryData.category.shortDescription) {
                        categoryData.category.shortDescription = categoryName;
                    }
                }
                mbpLogger.logInfo({
                    message: 'data returned from collection page query',
                });

                return (
                    <>
                        {categoryData?.showUnderNavText && <UnderNavTextWidgetContainer page={page} isCanadianCategory={data.findCategoryPageByUrl.content?.entries?.[0]?.country === 'Canada'} />}
                        <GraphqlCategoryBuilder
                            pageData={page}
                            categoryData={categoryData}
                            brand={data.brand}
                            presentation_family={presentation_family}
                        />
                    </>
                );
            }}
        </Query>
    );
}, stopReRender);

GraphqlCategoryPageContainer.propTypes = {
    isBot: bool,
    brand: object.isRequired,
    page: object.isRequired,
    userSubmittedZip: string.isRequired,
    locationType: string.isRequired,
    presentation_family: string.isRequired,
    currency: object.isRequired,
};

GraphqlCategoryPageContainer.defaultProps = {
    isBot: false,
};

const mapStateToProps = (state) => ({
    isBot: getIsBot(state),
    brand: getBrand(state),
    userSubmittedZip: getUserSubmittedProductFilterZipcode(state),
    locationType: getLocationType(state),
    presentation_family: getPresentationFamily(state),
    currency: getCurrencyData(state),
});

const enhance = compose(
    withPageView({
        pageViewSuccessTest: coreBrowsingPageViewSuccessTest,
    }),
    withRouter,
    connect(mapStateToProps),
);

export default enhance(GraphqlCategoryPageContainer);
